function parseVariantInventory(form) {
  const script = form?.querySelector('[data-ld-variant-inventory]');
  if (!script) return {};
  try {
    return JSON.parse(script.textContent);
  } catch {
    return {};
  }
}

function isVariantBackorder(info, quantity) {
  if (!info || info.inventory_management !== 'shopify' || info.inventory_policy !== 'continue') {
    return false;
  }
  const available = Number(info.inventory_quantity);
  const needed = Number(quantity) > 0 ? Number(quantity) : 1;
  return available < needed;
}

function leadTimeValue(info, quantity, fallbackUntracked = false) {
  if (info) {
    if (info.inventory_management !== 'shopify') return window.variantStrings?.btoLeadTime;
    if (isVariantBackorder(info, quantity)) return window.variantStrings?.backorderLeadTime;
    return null;
  }
  if (fallbackUntracked) return window.variantStrings?.btoLeadTime;
  return null;
}

function extraLeadTimeValue(checkbox) {
  if (!checkbox) return null;
  if (checkbox.dataset.bto === 'true') return window.variantStrings?.btoLeadTime;
  if (checkbox.dataset.backorder === 'true') return window.variantStrings?.backorderLeadTime;
  return null;
}

function leadTimeProperties(value) {
  const name = window.variantStrings?.leadTimeProperty;
  if (!name || !value) return {};
  return { [name]: value };
}

function withLeadTime(item, value) {
  const properties = leadTimeProperties(value);
  if (Object.keys(properties).length) item.properties = properties;
  return item;
}

function leadTimePropertyName(input) {
  return input.dataset.ldLeadTimeName || window.variantStrings?.leadTimeProperty;
}

function requestedQuantity(form) {
  const quantity = parseInt(form?.elements.namedItem('quantity')?.value, 10);
  return quantity > 0 ? quantity : 1;
}

function inStockLabel(info) {
  const template = window.variantStrings?.inStockWithCount;
  if (!template || !info) return null;
  const available = Number(info.inventory_quantity);
  const shown = available > 10 ? '10+' : String(Math.max(available, 0));
  return template.replace('__QTY__', shown);
}

function paintStockStatus(status, label, inStock) {
  status.classList.toggle('ld-stock--in', inStock);
  status.classList.toggle('ld-stock--none', !inStock);
  let icon = status.querySelector('.ld-stock__icon');
  if (!icon) {
    icon = document.createElement('span');
    icon.setAttribute('aria-hidden', 'true');
    status.prepend(icon);
  }
  icon.className = `ld-stock__icon ld-stock__icon--${inStock ? 'in' : 'none'}`;
  icon.textContent = inStock ? '\u2713' : '';
  let labelEl = status.querySelector('.ld-stock__label');
  if (!labelEl) {
    Array.from(status.childNodes).forEach((node) => {
      if (node !== icon && node.nodeType === Node.TEXT_NODE) node.remove();
    });
    labelEl = document.createElement('span');
    labelEl.className = 'ld-stock__label';
    status.append(labelEl);
  }
  labelEl.textContent = label;
}

function syncVisibleStock(form, info, quantity, leadValue) {
  const sectionId = form.closest('product-form')?.dataset.sectionId;
  if (!sectionId || form.id !== `product-form-${sectionId}`) return;
  const status = document.getElementById(`Inventory-${sectionId}`)?.querySelector('.ld-stock__status');
  if (!status || !info) return;
  const available = Number(info.inventory_quantity);
  const covered = info.inventory_management === 'shopify' && available >= quantity;
  if (covered) {
    const label = inStockLabel(info);
    if (label) paintStockStatus(status, label, true);
    return;
  }
  if (leadValue) paintStockStatus(status, leadValue, false);
}

function syncLeadTimeProperty(root = document) {
  root.querySelectorAll('[data-ld-lead-time-property]').forEach((input) => {
    const form = input.form;
    if (!form) return;
    const variantId = form.querySelector('[name="id"]')?.value;
    const quantity = requestedQuantity(form);
    const inventory = parseVariantInventory(form);
    const info = inventory[String(variantId)];
    const value = leadTimeValue(info, quantity, input.dataset.ldProductUntracked === 'true');
    const propertyName = leadTimePropertyName(input);
    if (value && propertyName) {
      input.name = `properties[${propertyName}]`;
      input.value = value;
      input.disabled = false;
    } else {
      input.removeAttribute('name');
      input.value = '';
      input.disabled = true;
    }
    syncVisibleStock(form, info, quantity, value);
  });
}

function syncBeforeExpressCheckout(event) {
  if (!event.target.closest?.('.shopify-payment-button, shopify-accelerated-checkout')) return;
  syncLeadTimeProperty();
}

document.addEventListener('pointerdown', syncBeforeExpressCheckout, true);
document.addEventListener('click', syncBeforeExpressCheckout, true);

window.ldSyncLeadTimeProperty = syncLeadTimeProperty;

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.syncLeadTimeProperty();
        this.form?.addEventListener('change', (event) => {
          if (event.target.name === 'id' || event.target.name === 'quantity') {
            this.syncLeadTimeProperty();
          }
        });
        this.form?.elements.namedItem('quantity')?.addEventListener('change', () => {
          this.syncLeadTimeProperty();
        });
        this.form?.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('.shopify-payment-button, shopify-accelerated-checkout')) {
              syncLeadTimeProperty();
            }
          },
          true
        );
      }

      syncLeadTimeProperty() {
        syncLeadTimeProperty();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.syncLeadTimeProperty();
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const extraVariantIds = Array.from(
          document.querySelectorAll('[data-optional-extras] .ld-extras__checkbox:checked:not(:disabled)')
        )
          .map((input) => Number(input.value))
          .filter((id) => id && String(id) !== String(variantId));

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        let requestBody;
        if (extraVariantIds.length) {
          const inventory = parseVariantInventory(this.form);
          const leadInput = this.form.querySelector('[data-ld-lead-time-property]');
          const payload = {
            items: [
              withLeadTime(
                { id: Number(variantId), quantity },
                leadTimeValue(
                  inventory[String(variantId)],
                  quantity,
                  leadInput?.dataset.ldProductUntracked === 'true'
                )
              ),
              ...extraVariantIds.map((id) => {
                const checkbox = document.querySelector(
                  `[data-optional-extras] .ld-extras__checkbox[data-variant-id="${id}"]`
                );
                return withLeadTime({ id, quantity: 1 }, extraLeadTimeValue(checkbox));
              }),
            ],
          };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
